import React from 'react';
import { ScaleLinear } from 'd3-scale';
import { line } from 'd3-shape';

import { SURFACE_TYPE } from '@strava/constants';
import { color } from '@strava/ui-tokens/js';

import styles from './SurfaceTypePath.module.scss';

interface SurfaceTypePathProps {
  xScale: ScaleLinear<number, number, never>;
  yScale: ScaleLinear<number, number, never>;
  stream: {
    convertedElevation: number[];
    convertedDistance: number[];
    surface?: number[] | string[] | null;
  };
  clipPath?: string;
}

interface FormatData {
  x: number;
  y: number;
}

export const SurfaceTypePath = React.memo(
  ({ stream, xScale, yScale, clipPath }: SurfaceTypePathProps) => {
    const bgLineData: FormatData[] = stream.convertedElevation.map(
      (elev, index) => ({
        x: stream.convertedDistance[index] ?? 0,
        y: elev
      })
    );

    const draw = line<FormatData>()
      .x((d) => xScale(d.x))
      .y((d) => yScale(d.y));

    let tmpIdx = 0;
    const surfacesData = stream.surface?.reduce<
      Array<{ type: number | string; data: Array<{ x: number; y: number }> }>
    >((acc, val, idx, arr) => {
      const next = arr[idx + 1];
      const x = stream.convertedDistance[idx];
      const y = stream.convertedElevation[idx];

      if (!acc[tmpIdx]) {
        acc[tmpIdx] = {
          type: val,
          data: []
        };
      }

      if (x !== undefined && y !== undefined) {
        acc[tmpIdx]?.data.push({ x, y });
      }

      if (val !== next) {
        tmpIdx += 1;
      }

      return acc;
    }, []);

    return (
      <g className={styles.surfaceType} clipPath={clipPath}>
        <path
          fill="none"
          stroke={color.colorCoreO3}
          strokeWidth={4}
          d={draw(bgLineData) ?? ''}
        />

        {surfacesData?.map((surface, index) => {
          if ([Number(SURFACE_TYPE.paved), 'Paved'].includes(surface.type)) {
            return (
              <path
                key={`${surface.type}-${index.toString()}`}
                fill="none"
                stroke={color.colorCoreO3}
                strokeWidth={2}
                d={draw(surface.data) ?? ''}
              />
            );
          }
          if (
            [Number(SURFACE_TYPE.unpaved), 'Unpaved'].includes(surface.type)
          ) {
            return (
              <path
                key={`${surface.type}-${index.toString()}`}
                fill="none"
                stroke={color.colorCoreWhite}
                strokeWidth={2}
                strokeDasharray="2 2.5"
                d={draw(surface.data) ?? ''}
              />
            );
          }

          return (
            <path
              key={`${surface.type}-${index.toString()}`}
              fill="none"
              stroke={color.colorCoreWhite}
              strokeWidth={2}
              d={draw(surface.data) ?? ''}
            />
          );
        })}
      </g>
    );
  }
);
